import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SiteHeader, SiteFooter, AgTagline } from '../components/SiteLayout';
import { Button } from '../components/ui/button';
import { ArrowRight } from '@phosphor-icons/react';
import TI from '../components/brand/TI';

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const linkTarget = isAuthenticated ? '/upload' : '/register';

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SiteHeader />

      {/* Hero */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            {/* Brand mark */}
            <div className="mb-6 flex items-baseline gap-3">
              <h2 className="text-5xl sm:text-6xl font-extrabold tracking-tight leading-none text-[#2A3547]">
                <TI color="#FF4E6A" />
              </h2>
              <span className="text-xs font-semibold tracking-[0.15em] text-[#4B5563] uppercase">
                Powered by Aᴳ
              </span>
            </div>
            <AgTagline className="text-sm font-semibold block mb-5 tracking-wide" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#111827] leading-[1.05] mb-6" data-testid="hero-headline">
              Toxicology intelligence for cosmetic and consumer-product brands
            </h1>
            <p className="text-base sm:text-lg text-[#4B5563] leading-relaxed mb-10 max-w-2xl">
              Formula risk intelligence, contaminant surveillance, Prop 65 risk triage, and exposure-based safety assessment — all in one B2B portal. Every formal assessment is personally signed by the toxicologists at A<sup>G</sup>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to={linkTarget}>
                <Button className="h-12 px-8 text-base bg-[#111827] text-white hover:bg-[#1F2937] rounded font-semibold" data-testid="hero-cta-start">
                  Start New Assessment <ArrowRight size={20} className="ml-2" weight="bold" />
                </Button>
              </Link>
              <Link to={isAuthenticated ? '/dashboard' : '/login'}>
                <Button variant="outline" className="h-12 px-8 text-base rounded border-[#111827] text-[#111827] hover:bg-[#111827] hover:text-white font-semibold" data-testid="hero-cta-login">
                  {isAuthenticated ? 'Go to Portal' : 'Client Sign In'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-6"><div className="border-t border-gray-200" /></div>

      {/* How It Works */}
      <section id="how-it-works" className="bg-[#FAFAFA] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#4B5563] mb-2">Process</h2>
          <p className="text-2xl sm:text-3xl font-bold tracking-tight text-[#111827] mb-3">How it works</p>
          <p className="text-sm text-[#4B5563] max-w-2xl mb-10">
            One portal. Six enterprise workflows. Every output is exposure-driven, evidence-graded, and built for regulatory, retailer, and counsel review.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Upload formulation', desc: 'Drop your ingredient list (CSV / Excel / manual). Tag product type, target population, claims, and supplier specifications.' },
              { step: '02', title: 'Pick a workflow', desc: 'Risk Screen for rapid triage · TRA/QRA for route-specific MoS · Full Toxicological Assessment (EC 1223 Part A + B) · Prop 65 · 16-section SDS · Claims substantiation.' },
              { step: '03', title: 'Aᴳ exposure & MoS engine runs', desc: 'INCI-level hazard intelligence with Klimisch reliability scoring; SED/MoS auto-calculated against population-specific defaults; contaminant pathway surveillance applied.' },
              { step: '04', title: 'Signed report delivered', desc: 'Download a Scientific Risk Memo, TRA/QRA report, Aᴳ-signed EC 1223 dossier, Prop 65 risk characterization, GHS SDS, or claims substantiation memo — versioned and audit-logged.' },
            ].map(item => (
              <div key={item.step} className="p-6 bg-white border border-gray-200 rounded">
                <span className="text-3xl font-black text-gray-200 tabular-nums block mb-3">{item.step}</span>
                <h3 className="font-bold text-[#111827] mb-2 text-sm">{item.title}</h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services — 6 enterprise workflows */}
      <section id="services" className="bg-white py-16 lg:py-20 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#4B5563] mb-2">Services</h2>
          <p className="text-2xl sm:text-3xl font-bold tracking-tight text-[#111827] mb-3">Six enterprise workflows</p>
          <p className="text-sm text-[#4B5563] max-w-2xl mb-10">
            Distinct outputs for distinct decisions — no duplication, no overlap. Pick the right scientific instrument for the question on the table.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* 1. Risk Screen */}
            <div className="p-6 border border-gray-200 rounded bg-white" data-testid="service-risk-screen">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#2563EB] mb-1">01 · Triage</p>
              <h3 className="text-base font-bold text-[#111827] mb-1"><TI color="#E63946" /> Risk Screen</h3>
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-3">3 business days · Scientific Risk Memo</p>
              <p className="text-sm text-[#4B5563] leading-relaxed mb-4">
                INCI-level hazard &amp; contaminant flagging (PFAS, phthalates, nitrosamines, 1,4-dioxane, benzene precursors). Six-tier escalation pathway.
              </p>
            </div>

            {/* 2. TRA / QRA */}
            <div className="p-6 border border-gray-200 rounded bg-white" data-testid="service-tra-qra">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#723B13] mb-1">02 · Exposure</p>
              <h3 className="text-base font-bold text-[#111827] mb-1">TRA / QRA</h3>
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-3">Project-dependent · Exposure-based MoS report</p>
              <p className="text-sm text-[#4B5563] leading-relaxed mb-4">
                Route-specific SED, NOAEL pulled from the Aᴳ Masterlist, Margin of Safety, and risk characterization per ingredient and per product.
              </p>
            </div>

            {/* 3. Full Toxicological Assessment */}
            <div className="p-6 border border-[#111827] rounded bg-[#111827] text-white" data-testid="service-full">
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/60 mb-1">03 · Sign-off</p>
              <h3 className="text-base font-bold mb-1">Full Toxicological Assessment</h3>
              <p className="text-[11px] font-bold uppercase tracking-wider text-white/50 mb-3">EC 1223 Part A + Part B · Aᴳ signed</p>
              <p className="text-sm text-white/70 leading-relaxed mb-4">
                Formal cosmetic safety assessment: composition, tox profile, exposure, MoS, safety conclusion, warnings — Aᴳ-signed CPSR-aligned dossier.
              </p>
            </div>

            {/* 4. Prop 65 */}
            <div className="p-6 border border-gray-200 rounded bg-white" data-testid="service-prop65">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#9B1C1C] mb-1">04 · Regulatory</p>
              <h3 className="text-base font-bold text-[#111827] mb-1">California Prop 65</h3>
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-3">Project-dependent · Exposure &amp; warning determination</p>
              <p className="text-sm text-[#4B5563] leading-relaxed mb-4">
                NSRL / MADL derivation, exposure-based risk characterization, and a defensible warning-decision rationale for products sold into California.
              </p>
            </div>

            {/* 5. SDS */}
            <div className="p-6 border border-gray-200 rounded bg-white" data-testid="service-sds">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#374151] mb-1">05 · Compliance</p>
              <h3 className="text-base font-bold text-[#111827] mb-1">Safety Data Sheet</h3>
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-3">Same-day · 16-section OSHA/GHS SDS</p>
              <p className="text-sm text-[#4B5563] leading-relaxed mb-4">
                GHS-aligned Safety Data Sheets per OSHA HazCom 2012, including H/P statement assignment, transport, ecological, and disposal sections.
              </p>
            </div>

            {/* 6. Claims */}
            <div className="p-6 border border-gray-200 rounded bg-white" data-testid="service-claims">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#0E9F6E] mb-1">06 · Marketing risk</p>
              <h3 className="text-base font-bold text-[#111827] mb-1">Claims Substantiation</h3>
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-3">3 business days · Risk memo per claim</p>
              <p className="text-sm text-[#4B5563] leading-relaxed mb-4">
                Pregnancy-safe, kids/teens-safe, sensitive-skin, hypoallergenic, fragrance-free — exposure-substantiated rationale for marketing &amp; retailer-facing claims.
              </p>
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Link to={linkTarget}>
              <Button className="h-11 px-6 text-sm bg-[#111827] text-white hover:bg-[#1F2937] rounded font-semibold" data-testid="services-cta-start">
                Start a project <ArrowRight size={18} className="ml-2" weight="bold" />
              </Button>
            </Link>
            <Link to={isAuthenticated ? '/dashboard' : '/login'}>
              <Button variant="outline" className="h-11 px-6 text-sm rounded border-[#111827] text-[#111827] hover:bg-[#111827] hover:text-white font-semibold" data-testid="services-cta-portal">
                {isAuthenticated ? 'Go to Portal' : 'Client Sign In'}
              </Button>
            </Link>
            <Link to="/portfolio">
              <Button variant="outline" className="h-11 px-6 text-sm rounded border-[#FF4E6A] text-[#FF4E6A] hover:bg-[#FF4E6A] hover:text-white font-semibold" data-testid="services-cta-portfolio">
                Request a portfolio
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="bg-white py-16 lg:py-20 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="inline-flex flex-col items-center">
            <span className="text-4xl font-extrabold tracking-tight leading-none text-[#2A3547]">
              <TI color="#FF4E6A" />
            </span>
            <span className="mt-2 text-[11px] font-semibold tracking-[0.15em] text-[#4B5563] uppercase">
              Powered by Aᴳ
            </span>
          </div>
          <p className="text-base text-[#4B5563] leading-relaxed mt-6 mb-8">
            Assessments are exposure-driven, quantitatively supported, and documented for regulatory review, retail compliance, and enforcement-relevant decision-making. Full safety assessments reviewed and signed by Augustine K. Agyekum, PhD — Principal Toxicologist, The Agyekum Group.
          </p>
          <Link to={isAuthenticated ? '/dashboard' : '/register'}>
            <Button className="h-12 px-8 text-base bg-[#111827] text-white hover:bg-[#1F2937] rounded font-semibold" data-testid="cta-bottom">
              {isAuthenticated ? 'Go to Portal' : 'Create Your Account'} <ArrowRight size={20} className="ml-2" weight="bold" />
            </Button>
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
